// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Theme } from '@mui/material/styles';

// ==============================|| TABLE PAGINATION (Mantis design) ||============================== //
// Standalone pagination: "Pe pagină" + "Mergi la" + numbered pagination (first/last).

interface Props {
	count: number; // total rows
	page: number; // 0-based index
	rowsPerPage: number;
	onPageChange: (page: number) => void; // 0-based
	onRowsPerPageChange: (rowsPerPage: number) => void;
	rowsPerPageOptions: number[];
}

export default function MantisPagination({
	count,
	page,
	rowsPerPage,
	onPageChange,
	onRowsPerPageChange,
	rowsPerPageOptions
}: Props) {
	const downSM = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	const pageCount = Math.max(1, Math.ceil(count / rowsPerPage));
	const current = Math.min(page, pageCount - 1) + 1; // 1-based, clamped

	const handlePageSize = (e: SelectChangeEvent<number>): void => {
		onRowsPerPageChange(Number(e.target.value));
		onPageChange(0);
	};

	const handleGoTo = (value: string): void => {
		const next = value ? Number(value) - 1 : 0;
		onPageChange(Math.min(Math.max(0, next), pageCount - 1));
	};

	return (
		<Stack
			direction="row"
			sx={{ gap: 2, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', px: 2.5, py: 1.5 }}
		>
			<Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
				<Typography variant="caption" sx={{ color: 'secondary.main' }}>
					Pe pagină
				</Typography>
				<Select
					value={rowsPerPage}
					onChange={handlePageSize}
					size="small"
					slotProps={{ input: { sx: { py: 0.75, px: 1.25 } } }}
				>
					{rowsPerPageOptions.map((option) => (
						<MenuItem key={option} value={option}>
							{option}
						</MenuItem>
					))}
				</Select>
				<Typography variant="caption" sx={{ color: 'secondary.main' }}>
					Mergi la
				</Typography>
				<TextField
					size="small"
					type="number"
					value={current}
					onChange={(e) => handleGoTo(e.target.value)}
					slotProps={{ htmlInput: { min: 1, max: pageCount, sx: { py: 0.75, px: 1.25, width: 40 } } }}
				/>
			</Stack>
			<Pagination
				sx={{ my: 0.5 }}
				count={pageCount}
				page={current}
				onChange={(_, value) => onPageChange(value - 1)}
				color="primary"
				variant="combined"
				showFirstButton={!downSM}
				showLastButton={!downSM}
				size={downSM ? 'small' : 'medium'}
			/>
		</Stack>
	);
}
